// מפרט מדיה. שדה חסר נשמר כ-unknown ולא כערך ברירת מחדל.
import { z } from 'zod';

export const SpecFieldKey = z.enum([
  'sizes',              // [{width,height}]
  'formats',            // ['html5','jpg','png','gif']
  'zipMaxKb',           // משקל ZIP דחוס
  'unzippedMaxKb',      // משקל לאחר חילוץ
  'weightBasis',        // 'files' | 'initialLoad' | 'totalLoad'
  'maxDurationMs',
  'maxLoops',
  'fps',
  'clickMethod',        // 'clickTag' | 'clickTAG' | 'exitApi' | 'other'
  'clickTagName',       // שם המשתנה המדויק
  'entryFile',          // index.html
  'allowExternalJs',
  'allowWebFonts',
  'allowSvg',
  'allowExternalRequests',
  'backupImageRequired',
  'backupImageFormat',
  'forbiddenFiles',     // ['.swf', ...]
  'packageNaming',
  'notes',
]);
export type SpecFieldKey = z.infer<typeof SpecFieldKey>;

/** שדות שבלעדיהם אי אפשר לייצא חבילה "מוכנה למסירה" */
export const REQUIRED_FOR_DELIVERY: SpecFieldKey[] = ['sizes', 'formats', 'zipMaxKb', 'clickMethod'];

export const SpecRuleSource = z.object({
  sourceId: z.string(),
  page: z.number().int().nullable(),
  quote: z.string().max(400),
  confidence: z.number().min(0).max(1),
  method: z.enum(['text', 'ocr', 'ai', 'manual']),
});

export const SpecRule = z.object({
  key: SpecFieldKey,
  value: z.unknown(),
  unit: z.string().nullable().default(null),
  sources: z.array(SpecRuleSource).default([]),
});
export type SpecRule = z.infer<typeof SpecRule>;

export interface SpecRuleMap { [k: string]: SpecRule | undefined }

export interface ResolvedSpec {
  sizes: { width: number; height: number }[] | 'unknown';
  formats: string[] | 'unknown';
  zipMaxKb: number | 'unknown';
  unzippedMaxKb: number | 'unknown';
  weightBasis: string | 'unknown';
  maxDurationMs: number | 'unknown';
  maxLoops: number | 'unknown';
  fps: number | 'unknown';
  clickMethod: string | 'unknown';
  clickTagName: string | 'unknown';
  entryFile: string | 'unknown';
  allowExternalJs: boolean | 'unknown';
  allowWebFonts: boolean | 'unknown';
  allowSvg: boolean | 'unknown';
  allowExternalRequests: boolean | 'unknown';
  backupImageRequired: boolean | 'unknown';
  backupImageFormat: string | 'unknown';
  forbiddenFiles: string[] | 'unknown';
  packageNaming: string | 'unknown';
  notes: string | 'unknown';
}

export function resolveSpec(rules: SpecRule[]): ResolvedSpec {
  const out: Record<string, unknown> = {};
  for (const k of SpecFieldKey.options) out[k] = 'unknown';
  for (const r of rules) if (r.value !== undefined && r.value !== null) out[r.key] = r.value;
  return out as unknown as ResolvedSpec;
}

export function missingRequired(spec: ResolvedSpec): SpecFieldKey[] {
  return REQUIRED_FOR_DELIVERY.filter((k) => spec[k as keyof ResolvedSpec] === 'unknown');
}

export const SPEC_FIELD_LABELS: Record<SpecFieldKey, string> = {
  sizes: 'מידות מותרות', formats: 'פורמטים', zipMaxKb: 'משקל ZIP מרבי (KB)', unzippedMaxKb: 'משקל לאחר חילוץ (KB)',
  weightBasis: 'בסיס המשקל', maxDurationMs: 'משך אנימציה מרבי (ms)', maxLoops: 'לולאות מרביות', fps: 'FPS',
  clickMethod: 'שיטת קליק', clickTagName: 'שם משתנה clickTag', entryFile: 'קובץ כניסה', allowExternalJs: 'JS חיצוני מותר',
  allowWebFonts: 'פונטים מוטמעים מותרים', allowSvg: 'SVG מותר', allowExternalRequests: 'בקשות חיצוניות מותרות',
  backupImageRequired: 'נדרשת תמונת גיבוי', backupImageFormat: 'פורמט תמונת גיבוי', forbiddenFiles: 'קבצים אסורים',
  packageNaming: 'דרישת שם חבילה', notes: 'הערות',
};
